import React, { useState, useEffect, useRef } from "react";
import { Box, Button, Stack, Typography } from "@mui/material";
import { Formik, Form } from "formik";
import { routes } from "../../../constants/routes";
import ProfileHeader from "../../../common/components/profile-header/profile-header-and-breadcrumbs";
import ServicePackageAccordion from "./service-accordion";
import { AlertComponent } from "../../../common/components";
import {
  createService,
  getPackages,
  updateService,
  getPackageById,
} from "../../../api/preneed-section/preneed-payments-orders.api";
import { getCategories } from "../../../api/admin/service-management";
import {
  initialValues,
  validationSchema,
  formatPayload,
  mapBackendDataToForm,
  buildUpdatePayload,
  mapSinglePackageToForm,
} from "./create-service-helper";
import "./create-services.scss";
interface Props {
  mode?: "create" | "edit" | "view";
}

const CreatePackage: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [alertOpen, setAlertOpen] = useState(false);
  const [alertMessage, setAlertMessage] = useState("");
  const [alertSeverity, setAlertSeverity] = useState<
    "error" | "success" | "info" | "warning"
  >("info");

  const [mode, setMode] = useState<"create" | "view" | "edit">("create");
  const [editingPackages, setEditingPackages] = useState<
    Record<"Basic" | "Standard" | "Premium", boolean>
  >({
    Basic: false,
    Standard: false,
    Premium: false,
  });
  const [packageIds, setPackageIds] = useState<{
    Basic?: number;
    Standard?: number;
    Premium?: number;
  }>({});
  const [packageLoading, setPackageLoading] = useState<
    Record<"Basic" | "Standard" | "Premium", boolean>
  >({
    Basic: false,
    Standard: false,
    Premium: false,
  });

  const formRef = useRef<any>(null);
  const [formInitialValues, setFormInitialValues] = useState(initialValues);
  const [serviceCategories, setServiceCategories] = useState<any[]>([]);
  const [isInitializing, setIsInitializing] = useState(true);

  const packages = [
    { key: "Basic", step: 1, title: "Basic Package" },
    { key: "Standard", step: 2, title: "Standard Package" },
    { key: "Premium", step: 3, title: "Premium Package" },
  ];

  const fetchCategories = async () => {
    try {
      const res = await getCategories({ type: "all" });
      const categories = res?.data?.data?.data ?? [];
      const preneedCategories = categories.filter((c: any) =>
        c.needTypes?.includes("pre_need"),
      );
      setServiceCategories(
        preneedCategories.map((c: any) => ({ label: c.name, id: c.id })),
      );
    } catch (err) {
      console.error("Error fetching categories:", err);
    }
  };

  const init = React.useCallback(async () => {
    setIsInitializing(true);
    await fetchCategories();

    try {
      const res = await getPackages();
      const packagesData = res?.data?.data?.data ?? [];

      if (packagesData.length > 0) {
        const mappedData = mapBackendDataToForm(packagesData);
        setPackageIds({
          Basic: packagesData.find((p) => p.name === "Basic")?.id,
          Standard: packagesData.find((p) => p.name === "Standard")?.id,
          Premium: packagesData.find((p) => p.name === "Premium")?.id,
        });
        setFormInitialValues(mappedData);
        setMode("view");
      } else {
        setFormInitialValues(initialValues);
        setMode("create");
      }
    } catch (err) {
      console.error("Error fetching packages:", err);
      setMode("create");
    } finally {
      setEditingPackages({
        Basic: false,
        Standard: false,
        Premium: false,
      });
      setIsInitializing(false);
    }
  }, []);

  useEffect(() => {
    init();
  }, [init]);

  const handleFormSubmit = async (values: typeof initialValues) => {
    const allPackagesDisabled =
    !values.packages.Basic.isActive &&
    !values.packages.Standard.isActive &&
    !values.packages.Premium.isActive;

  if (allPackagesDisabled) {
    setAlertMessage("Please enable at least one package to continue");
    setAlertSeverity("error");
    setAlertOpen(true);
    return;
  }
    const payload = formatPayload(values);
    try {
      setLoading(true);
      const response = await createService(payload);
      if (response.data?.success) {
        setAlertMessage("Service packages have been created successfully.");
        setAlertSeverity("success");
        setAlertOpen(true);
        formRef.current?.resetForm();
        await init();
      }
    } catch (err: any) {
      setAlertMessage(err?.response?.data?.message || "Failed to submit");
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setLoading(false);
    }
  };
  const handleUpdatePackage = async (
    packageKey: "Basic" | "Standard" | "Premium",
    values: typeof initialValues,
  ) => {
    const originalPkg = formInitialValues.packages[packageKey];
    const currentPkg = values.packages[packageKey];
    const packageId = packageIds[packageKey];

    if (!packageId) return;

    const payload = buildUpdatePayload(originalPkg, currentPkg);
    if (Object.keys(payload).length === 0) {
      setEditingPackages((prev) => ({ ...prev, [packageKey]: false }));
      setMode("view");
      return;
    }

    try {
      setPackageLoading((prev) => ({ ...prev, [packageKey]: true }));
      await updateService(packageId, payload);
      const res = await getPackageById(packageId);
      const freshPkg = res?.data?.data?.data;

      if (!freshPkg) return;

      const mappedPkg = mapSinglePackageToForm(freshPkg);
      setFormInitialValues((prev) => ({
        ...prev,
        packages: {
          ...prev.packages,
          [packageKey]: mappedPkg,
        },
      }));
      formRef.current?.setFieldValue(
        `packages.${packageKey}`,
        mappedPkg,
        false,
      );
      setEditingPackages((prev) => ({
        ...prev,
        [packageKey]: false,
      }));
      setMode("view");
      setAlertMessage(`${packageKey} package updated successfully`);
      setAlertSeverity("success");
      setAlertOpen(true);
    } catch (err: any) {
      setAlertMessage(
        err?.response?.data?.message || "Failed to update package",
      );
      setAlertSeverity("error");
      setAlertOpen(true);
    } finally {
      setPackageLoading((prev) => ({ ...prev, [packageKey]: false }));
    }
  };
  const handleCancelPackageEdit = (
    packageKey: "Basic" | "Standard" | "Premium",
  ) => {
    formRef.current?.setFieldValue(
      `packages.${packageKey}`,
      formInitialValues.packages[packageKey],
    );
    setEditingPackages((prev) => ({
      ...prev,
      [packageKey]: false,
    }));
    setMode("view");
  };

  if (isInitializing) {
    return (
      <Box
        className="create-service-main-wrapper"
        display="flex"
        alignItems="center"
        justifyContent="center"
        minHeight="60vh"
      >
        <Typography variant="body1">Loading service packages...</Typography>
      </Box>
    );
  }

  return (
    <Box className="create-service-main-wrapper">
      <ProfileHeader
        breadcrumbLink={[
          { label: "Service Package", link: routes.PRENEED_SERVICE_PACKAGE },
        ]}
        mode="preNeed"
      />

      <Formik
        initialValues={formInitialValues}
        validationSchema={validationSchema}
        onSubmit={handleFormSubmit}
        enableReinitialize
        innerRef={formRef}
      >
        {({ dirty, isValid, resetForm }) => (
          <Form className="cards-wrapper">
            <Typography variant="h6" className="services-offerings__title">
              Service Packages
            </Typography>
            <Box mb={4}>
              <Typography
                variant="body2"
                className="services-offerings__subtitle"
              >
                Create your service packages by filling in the details for each
                tier. You can create one, two, or all three packages.
              </Typography>
            </Box>

            {packages.map((pkg) => (
              <ServicePackageAccordion
                key={pkg.key}
                packageKey={pkg.key as any}
                step={pkg.step}
                title={pkg.title}
                serviceCategories={serviceCategories}
                mode={
                  editingPackages[pkg.key as keyof typeof editingPackages]
                    ? "edit"
                    : mode
                }
                isPackageEditing={
                  editingPackages[pkg.key as keyof typeof editingPackages]
                }
                originalPackage={formInitialValues.packages[pkg.key as any]}
                onEdit={() => {
                  setEditingPackages({
                    Basic: false,
                    Standard: false,
                    Premium: false,
                    [pkg.key]: true,
                  });
                }}
                onSave={(key) =>
                  handleUpdatePackage(key, formRef.current.values)
                }
                isSaving={packageLoading[pkg.key as any]}
                onCancel={() =>
                  handleCancelPackageEdit(
                    pkg.key as "Basic" | "Standard" | "Premium",
                  )
                }
              />
            ))}

            {mode === "create" && (
              <Box
                className="create-service-action-buttons"
                mt={3}
                display="flex"
                gap={2}
              >
                <Button
                  className="cancel-btn"
                  variant="outlined"
                  color="secondary"
                  disabled={!dirty}
                  onClick={() => {
                    resetForm();
                  }}
                >
                  Reset All
                </Button>

                <Button
                  className="save-btn"
                  variant="contained"
                  color="primary"
                  type="submit"
                  disabled={!isValid || !dirty}
                >
                  Create Packages
                </Button>
              </Box>
            )}
          </Form>
        )}
      </Formik>

      <AlertComponent
        open={alertOpen}
        message={alertMessage}
        type={alertSeverity}
        onClose={() => setAlertOpen(false)}
      />
    </Box>
  );
};

export default CreatePackage;
